import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useCare } from '../../hooks/useCare'
import { useI18n } from '../../hooks/useI18n'
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../../config/firebase'
import { InviteRequest, getDefaultPermissions } from '../../types/user'

interface InviteRegisterFormProps {
  inviteCode: string
  onBack: () => void
}

const InviteRegisterForm: React.FC<InviteRegisterFormProps> = ({ 
  inviteCode, 
  onBack 
}) => {
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [caseInfo, setCaseInfo] = useState<any>(null)
  const [loadingCase, setLoadingCase] = useState(true)
  const [inviteType, setInviteType] = useState<'caregiver' | 'assisted'>('caregiver')

  const { register } = useAuth()
  const { loadUserCases } = useCare()
  const { t } = useI18n()

  // Cargar información del caso desde el código de invitación
  useEffect(() => {
    const loadCaseInfo = async () => {
      try {
        console.log('Loading case info for invite code:', inviteCode)
        
        // Intentar buscar caso por código de invitación normal
        let casesQuery = query(
          collection(db, 'care-cases'),
          where('inviteCode', '==', inviteCode),
          where('isActive', '==', true)
        )
        
        let casesSnapshot = await getDocs(casesQuery)
        let foundAsCaregiver = true
        
        // Si no se encuentra, buscar por código de invitación de asistido
        if (casesSnapshot.empty) {
          casesQuery = query(
            collection(db, 'care-cases'),
            where('profileInviteCode', '==', inviteCode),
            where('isActive', '==', true)
          )
          
          casesSnapshot = await getDocs(casesQuery)
          foundAsCaregiver = false
        }
        
        if (casesSnapshot.empty) {
          throw new Error('Código de invitación inválido o expirado')
        }
        
        const caseDoc = casesSnapshot.docs[0]
        const caseData = {
          id: caseDoc.id,
          ...caseDoc.data(),
          createdAt: caseDoc.data().createdAt?.toDate() || new Date(),
          updatedAt: caseDoc.data().updatedAt?.toDate() || new Date()
        }
        
        setCaseInfo(caseData)
        setInviteType(foundAsCaregiver ? 'caregiver' : 'assisted')
        
      } catch (error) {
        console.error('Error loading case info:', error)
        setError('Código de invitación inválido o expirado')
      } finally {
        setLoadingCase(false)
      }
    }

    if (inviteCode) {
      loadCaseInfo()
    }
  }, [inviteCode])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden')
      setLoading(false)
      return
    }

    if (!caseInfo) {
      setError('Información del caso no disponible')
      setLoading(false)
      return
    }

    try {
      // 1. Registrar el usuario
      console.log('🔄 Registrando usuario con código de invitación:', inviteCode)
      await register(email, password, username)
      
      // 2. Esperar un momento para que el contexto de auth se actualice
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // El usuario ya está registrado, ahora el auto-join se activará automáticamente
      // en App.tsx cuando detecte el código de invitación
      
      console.log('✅ Usuario registrado exitosamente con código de invitación')
      
    } catch (error) {
      console.error('Register with invite error:', error)
      setError(error instanceof Error ? error.message : 'Error al crear la cuenta. Intenta nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  if (loadingCase) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Cargando información del caso...
          </p>
        </div>
      </div>
    )
  }

  if (!caseInfo) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Código de Invitación Inválido
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            El código de invitación no es válido o ha expirado.
          </p>
          <button
            onClick={onBack}
            className="btn btn-secondary px-4 py-2"
          >
            Volver
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {inviteType === 'assisted' 
            ? '¡Este es tu perfil personal de medicamentos!' 
            : '¡Has sido invitado a un caso de cuidado!'}
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          {inviteType === 'assisted' 
            ? `Te invitaron a acceder a tu perfil personal: "${caseInfo.profileInfo?.name || caseInfo.name}"`
            : `Te invitaron a unirte al caso: "${caseInfo.name}"`}
        </p>
        
        <div className={`${inviteType === 'assisted' ? 'bg-teal-50 dark:bg-teal-900/20' : 'bg-blue-50 dark:bg-blue-900/20'} p-4 rounded-lg mb-4`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${inviteType === 'assisted' ? 'bg-teal-100' : 'bg-blue-100'} rounded-lg flex items-center justify-center`}>
              {inviteType === 'assisted' ? (
                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <div className="text-left">
              <p className={`text-sm ${inviteType === 'assisted' ? 'text-teal-800 dark:text-teal-200' : 'text-blue-800 dark:text-blue-200'} font-medium`}>
                {inviteType === 'assisted' ? '🙋‍♂️ Persona Asistida' : '💡 Registro automático'}
              </p>
              <p className={`text-xs ${inviteType === 'assisted' ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300'}`}>
                {inviteType === 'assisted' 
                  ? `Al crear tu cuenta, podrás ver tu historial personal de medicamentos y marcarlos como tomados`
                  : `Al crear tu cuenta, te unirás automáticamente al caso "${caseInfo.name}"`}
              </p>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {inviteType === 'assisted' 
            ? 'Crea tu cuenta para acceder a tu perfil personal de medicamentos'
            : 'Crea tu cuenta para acceder al caso de cuidado'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('email')}
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('username')}
          </label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('password')}
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('confirm-password')}
          </label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
            <div className="flex">
              <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="ml-2 text-sm text-red-800 dark:text-red-200">
                {error}
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            ← Volver
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Registrando...' : 'Crear Cuenta y Unirse'}
          </button>
        </div>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          ¿Ya tienes cuenta?{' '}
          <button
            onClick={onBack}
            className="text-primary hover:text-primary/80"
          >
            Inicia sesión aquí
          </button>
        </p>
      </div>
    </div>
  )
}

export default InviteRegisterForm 